import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { lazyToast as toast } from "@/lib/lazyToast";

export interface Banner {
  id: string;
  title: string;
  image_url: string;
  image_url_mobile: string | null;
  link_url: string | null;
  open_in_new_tab: boolean;
  bg_color: string;
  starts_at: string;
  ends_at: string | null;
  is_active: boolean;
  sort_order: number;
  brand_id: string | null;
  created_at: string;
  updated_at: string;
}

export type BannerInsert = Omit<Banner, "id" | "created_at" | "updated_at">;

export function useActiveBanners(options?: { enabled?: boolean }) {
  return useQuery({
    enabled: options?.enabled ?? true,
    queryKey: ["banners", "active"],
    queryFn: async () => {

      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from("banners")
        .select("*")
        .eq("is_active", true)
        .lte("starts_at", now)
        .or(`ends_at.is.null,ends_at.gte.${now}`)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data as Banner[];
    },
    staleTime: 60 * 1000,
  });
}

export function useAllBanners() {
  return useQuery({
    queryKey: ["banners", "all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("banners")
        .select("*")
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data as Banner[];
    },
  });
}

export function useCreateBanner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (banner: BannerInsert) => {
      const { data, error } = await supabase.from("banners").insert(banner).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["banners"] });
      toast.success("Banner criado com sucesso");
    },
    onError: (e: Error) => toast.error("Erro ao criar banner: " + e.message),
  });
}

export function useUpdateBanner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Banner> & { id: string }) => {
      const { data, error } = await supabase.from("banners").update(updates).eq("id", id).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["banners"] });
      toast.success("Banner atualizado");
    },
    onError: (e: Error) => toast.error("Erro ao atualizar banner: " + e.message),
  });
}

export function useDeleteBanner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("banners").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["banners"] });
      toast.success("Banner excluído");
    },
    onError: (e: Error) => toast.error("Erro ao excluir: " + e.message),
  });
}

function resizeToSquare(file: File, size = 360): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d")!;
      // transparent background, contain the image centered
      const scale = Math.min(size / img.width, size / img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Canvas toBlob failed"));
      }, "image/png");
    };
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = URL.createObjectURL(file);
  });
}

export async function uploadBannerImage(file: File): Promise<string> {
  const blob = await resizeToSquare(file, 360);
  const path = `${crypto.randomUUID()}.png`;
  const { error } = await supabase.storage.from("banners").upload(path, blob, { upsert: true, contentType: "image/png" });
  if (error) throw error;
  const { data } = supabase.storage.from("banners").getPublicUrl(path);
  return data.publicUrl;
}
